import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { REDO_EFFECT_API_URL } from "@redotech/redo-merchant-app-common/config";
import { EffectApiClientProvider } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { SettingNavigationProvider } from "@redotech/redo-merchant-app-common/global-search/setting-search/setting-navigator-context";
import { RouterProtector } from "@redotech/redo-merchant-app-common/routing/router-protector";
import { allMerchantAppSettings } from "@redotech/redo-merchant-app-common/setting/settings";
import { StreamProvider } from "@redotech/redo-merchant-app-common/stream/stream-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  DocumentTitleOverriders,
  DocumentTitleOverridersContext,
} from "@redotech/redo-merchant-app-common/title";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { StreamingRouteTracker } from "@redotech/redo-merchant-fulfillment-app/common/streaming-enabled-context";
import { BillingFailedAlert } from "@redotech/redo-merchant-general-app/billing/failed-alert";
import {
  BreadcrumbOverriders,
  BreadcrumbOverridersContext,
  useBreadcrumbs,
} from "@redotech/redo-web/breadcrumb";
import { ThursdayBootWarningBanner } from "@redotech/redo-web/thursday-boot-warning-banner";
import { memo, useContext, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { useDocumentTitle } from "usehooks-ts";
import { OrderSyncProgress } from "./order-sync-progress";
import { getStoreScopedRedirectPath } from "./root-redirect";
import { WalkthroughProvider } from "./walkthrough/walkthrough-context";

const RouterRootInner = memo(function RouterRootInner() {
  const intercom = useIntercom();
  const navigate = useNavigate();
  const { teamId } = useParams();
  const url = useLocation();
  const user = useContext(UserContext);
  const team = useContext(TeamContext);

  const auth = useContext(AuthContext);

  useMemo(() => {
    if (auth && auth.teamId && !teamId) {
      const isTokenLogin = url.pathname === "/login/token";

      if (isTokenLogin) {
        const urlParams = new URLSearchParams(url.search);
        const nextParam = urlParams.get("next") || "";

        navigate(getStoreScopedRedirectPath(auth.teamId, nextParam), {
          replace: true,
        });
      } else {
        navigate(getStoreScopedRedirectPath(auth.teamId, url.pathname), {
          replace: true,
        });
      }
    }
  }, [auth, teamId, url.pathname, navigate, url.search]);

  const breadcrumbs = useBreadcrumbs();

  const [documentTitleOverriders] = useContext(DocumentTitleOverridersContext);
  const title = useMemo(() => {
    if (documentTitleOverriders && documentTitleOverriders.size > 0) {
      const firstOverrider = Array.from(documentTitleOverriders.values())[0];
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      return firstOverrider(breadcrumbs || []);
    }
    return breadcrumbs
      ? `${breadcrumbs.map((breadcrumb) => breadcrumb.name).join(" ")} — Redo`
      : "Redo";
  }, [breadcrumbs, documentTitleOverriders]);

  useDocumentTitle(title);

  useEffect(() => {
    // TODO(paul): more robust way to exclude Redo users
    if (
      !user ||
      !team ||
      user.email.endsWith("@getredo.com") ||
      user.email.endsWith("@redo.com")
    ) {
      return;
    }
    intercom.update({
      company: {
        companyId: team._id,
        name: team.name,
        createdAt: team.createdAt,
        website: `https://${team.storeUrl}/`,
      },
      createdAt: user.createdAt,
      email: user.email,
      name: user.name,
      userId: user._id,
    });
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, team]);

  return (
    <StreamingRouteTracker>
      <EffectApiClientProvider
        authToken={auth?.token}
        baseUrl={REDO_EFFECT_API_URL}
      >
        <StreamProvider>
          <WalkthroughProvider>
            <ThursdayBootWarningBanner email={user?.email} teamId={team?._id} />
            <OrderSyncProgress />
            <BillingFailedAlert />
            <SettingNavigationProvider settings={allMerchantAppSettings}>
              <RouterProtector>
                <Outlet />
              </RouterProtector>
            </SettingNavigationProvider>
          </WalkthroughProvider>
        </StreamProvider>
      </EffectApiClientProvider>
    </StreamingRouteTracker>
  );
});

export const RouterRoot = memo(function RouterRoot() {
  const [breadcrumbOverriders, setBreadcrumbOverriders] =
    useState<BreadcrumbOverriders>(new Map());
  const [documentTitleOverriders, setDocumentTitleOverriders] =
    useState<DocumentTitleOverriders>(new Map());
  return (
    <BreadcrumbOverridersContext.Provider
      value={[breadcrumbOverriders, setBreadcrumbOverriders]}
    >
      <DocumentTitleOverridersContext.Provider
        value={[documentTitleOverriders, setDocumentTitleOverriders]}
      >
        <RouterRootInner />
      </DocumentTitleOverridersContext.Provider>
    </BreadcrumbOverridersContext.Provider>
  );
});
